import { z } from "zod";

import { ApiError, apiClient, apiErrorCode } from "./api";

/**
 * Wire schemas and client for `/admin/sensitive-data` (openspec `add-sensitive-data-review`,
 * design D12). Nothing here ever carries a sensitive value: the API sends masked previews and
 * masked context only.
 *
 * Read schemas are NOT `.strict()`: a field the backend adds later must not blank the tab. A
 * Pydantic-to-Zod contract test reads these literals, so keep each nested object in its own named
 * constant, with its keys indented two spaces.
 */

/** The query-key root of every sensitive-data query, so one invalidation reaches them all and the
 * repository switch (`SCOPED_QUERY_ROOTS`) refetches them. */
export const SENSITIVE_DATA_QUERY_ROOT = "sensitive-data";

/** Query keys under that root. The settings sidebar reads the same `summary` key as the tab, so
 * the two share one request. Each key carries the repository scope. */
export const sensitiveDataKeys = {
  all: [SENSITIVE_DATA_QUERY_ROOT] as const,
  summary: (scopeKey: string) => [SENSITIVE_DATA_QUERY_ROOT, "summary", scopeKey] as const,
  scan: (scopeKey: string) => [SENSITIVE_DATA_QUERY_ROOT, "scan", scopeKey] as const,
  findings: (scopeKey: string) => [SENSITIVE_DATA_QUERY_ROOT, "findings", scopeKey] as const,
  locations: (id: string) => [SENSITIVE_DATA_QUERY_ROOT, "locations", id] as const,
  events: (scopeKey: string) => [SENSITIVE_DATA_QUERY_ROOT, "events", scopeKey] as const,
  scans: (scopeKey: string) => [SENSITIVE_DATA_QUERY_ROOT, "scans", scopeKey] as const,
};

export const findingStatusSchema = z.enum([
  "open",
  "suppressed_by_model",
  "censored",
  "deleted",
  "false_positive",
  "resolved",
]);
export type FindingStatus = z.infer<typeof findingStatusSchema>;

export const findingCategorySchema = z.enum(["secret", "personal_data"]);
export type FindingCategory = z.infer<typeof findingCategorySchema>;

export const findingFieldSchema = z.enum(["raw_content", "user_metadata", "source_ref"]);
export type FindingField = z.infer<typeof findingFieldSchema>;

export const detectorSchema = z.enum(["pattern", "model", "both"]);
export type Detector = z.infer<typeof detectorSchema>;

export const modelVerdictSchema = z.enum(["real", "example", "public_contact", "unsure"]);
export type ModelVerdict = z.infer<typeof modelVerdictSchema>;

export const upstreamSourceKindSchema = z.enum([
  "notion",
  "shortcut",
  "postman",
  "url",
  "s3",
  "self",
  "jira",
  "none",
]);
export type UpstreamSourceKind = z.infer<typeof upstreamSourceKindSchema>;

export const findingActionSchema = z.enum([
  "censor",
  "delete",
  "false_positive",
  "reopen",
  "promote",
  "revert_censor",
]);
export type FindingAction = z.infer<typeof findingActionSchema>;

export const cleanupStatusSchema = z.enum(["none", "pending", "done", "failed"]);
export type CleanupStatus = z.infer<typeof cleanupStatusSchema>;

/** Where to clean the origin of a finding: a censor only protects the RAG copy. */
export const upstreamGuidanceSchema = z.object({
  source_kind: upstreamSourceKindSchema,
  message_es: z.string(),
  external_url: z.string().nullable(),
});
export type UpstreamGuidance = z.infer<typeof upstreamGuidanceSchema>;

export const findingSchema = z.object({
  id: z.guid(),
  knowledge_item_id: z.guid(),
  // The item's `source_ref` with every detected span blocked.
  document_title: z.string(),
  source: z.string(),
  item_status: z.string(),
  kind: z.string(),
  kind_label: z.string(),
  category: findingCategorySchema,
  field: findingFieldSchema,
  masked_preview: z.string(),
  masked_context: z.string(),
  detector: detectorSchema,
  model_verdict: modelVerdictSchema.nullable(),
  model_reason_es: z.string().nullable(),
  status: findingStatusSchema,
  occurrences_count: z.number().int(),
  // Other items with a non-terminal finding of the same fingerprint.
  same_value_documents: z.number().int(),
  // The masked context as it will read after censoring, with `[censurado: <tipo>]` in place; null
  // when `censor` is not allowed.
  censor_preview: z.string().nullable(),
  upstream_guidance: upstreamGuidanceSchema,
  actions_allowed: z.array(findingActionSchema),
  cleanup_status: cleanupStatusSchema,
  resolved_by_email: z.string().nullable(),
  resolution_reason: z.string().nullable(),
  resolved_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Finding = z.infer<typeof findingSchema>;

export const findingListSchema = z.object({
  items: z.array(findingSchema),
  total: z.number().int(),
});
export type FindingList = z.infer<typeof findingListSchema>;

export const locationKindSchema = z.enum([
  "chat_answers",
  "feedback",
  "reports",
  "jira_notes",
  "user_memory",
  "chat_images",
  "report_images",
]);
export type LocationKind = z.infer<typeof locationKindSchema>;

/** One place, outside the knowledge item, where the same value was found. Nothing there is
 * cleaned: the counts are evidence of what a censor leaves behind. */
export const locationSchema = z.object({
  location: locationKindSchema,
  count: z.number().int(),
  timed_out: z.boolean(),
});
export type Location = z.infer<typeof locationSchema>;

export const locationListSchema = z.object({
  locations: z.array(locationSchema),
});
export type LocationList = z.infer<typeof locationListSchema>;

export const scanStatusSchema = z.enum(["queued", "running", "done", "failed"]);
export type ScanStatus = z.infer<typeof scanStatusSchema>;

export const scanTriggerKindSchema = z.enum(["cron", "manual", "incremental"]);
export type ScanTriggerKind = z.infer<typeof scanTriggerKindSchema>;

export const scanSchema = z.object({
  id: z.guid(),
  status: scanStatusSchema,
  trigger: scanTriggerKindSchema,
  detector_version: z.string(),
  items_considered: z.number().int(),
  items_scanned: z.number().int(),
  items_skipped_budget: z.number().int(),
  findings_opened: z.number().int(),
  findings_auto_resolved: z.number().int(),
  emails_ignored_by_domain: z.number().int(),
  model_calls: z.number().int(),
  errors: z.number().int(),
  error: z.string().nullable(),
  triggered_by_email: z.string().nullable(),
  created_at: z.string(),
  started_at: z.string().nullable(),
  finished_at: z.string().nullable(),
});
export type Scan = z.infer<typeof scanSchema>;

export const scanListSchema = z.object({
  items: z.array(scanSchema),
  next_cursor: z.string().nullable(),
});
export type ScanList = z.infer<typeof scanListSchema>;

/** How much of the corpus the detector has looked at, at its current version. */
export const coverageSchema = z.object({
  items_total: z.number().int(),
  items_scanned_current: z.number().int(),
  items_waiting_budget: z.number().int(),
  items_with_unreviewed_embedded_images: z.number().int(),
});
export type Coverage = z.infer<typeof coverageSchema>;

/** One kind of the detector's catalog, for the "tipo" filter. */
export const kindEntrySchema = z.object({
  kind: z.string(),
  kind_label: z.string(),
  category: findingCategorySchema,
});
export type KindEntry = z.infer<typeof kindEntrySchema>;

export const summarySchema = z.object({
  open_secrets: z.number().int(),
  open_personal_data: z.number().int(),
  suppressed_by_model: z.number().int(),
  censored: z.number().int(),
  deleted: z.number().int(),
  false_positive: z.number().int(),
  resolved: z.number().int(),
  coverage: coverageSchema,
  last_scan: scanSchema.nullable(),
  active_scan: scanSchema.nullable(),
  scan_enabled: z.boolean(),
  // The whole catalog, not only the kinds with findings.
  kinds: z.array(kindEntrySchema),
});
export type Summary = z.infer<typeof summarySchema>;

// The event `action` is deliberately a plain string: the ledger of a newer backend can record an
// action this build does not know, and one such row must not blank the whole "Registro".
export const eventSchema = z.object({
  id: z.guid(),
  finding_id: z.guid().nullable(),
  knowledge_item_id: z.guid().nullable(),
  scan_id: z.guid().nullable(),
  action: z.string(),
  actor_email: z.string().nullable(),
  is_system: z.boolean(),
  reason: z.string().nullable(),
  kind: z.string().nullable(),
  masked_preview: z.string().nullable(),
  document_title: z.string().nullable(),
  counts: z.record(z.string(), z.number().int()),
  created_at: z.string(),
});
export type Event = z.infer<typeof eventSchema>;

export const eventListSchema = z.object({
  items: z.array(eventSchema),
  next_cursor: z.string().nullable(),
});
export type EventList = z.infer<typeof eventListSchema>;

/** What became of a document's text when a censor was reverted (openspec
 * `prove-resolution-and-revert-censor`, design D5/D6): `restored` from the source, `unchanged`
 * because the source no longer holds the value, `failed` to re-sync it, `next_sync` (an S3
 * document comes back with the next sync), or `not_available` (the source cannot be re-synced, so
 * the text stays censored). */
export const textRestoreSchema = z.enum([
  "restored",
  "unchanged",
  "failed",
  "next_sync",
  "not_available",
]);
export type TextRestore = z.infer<typeof textRestoreSchema>;

/** The outcome of one finding of a bulk action: each is processed and audited on its own. The text
 * restoration only comes with `revert_censor`; any other action leaves it null or out. */
export const bulkResultItemSchema = z.object({
  finding_id: z.guid(),
  ok: z.boolean(),
  error_code: z.string().nullable(),
  finding: findingSchema.nullable(),
  text_restore: textRestoreSchema.nullable().optional(),
});
export type BulkResultItem = z.infer<typeof bulkResultItemSchema>;

export const bulkResultSchema = z.object({
  results: z.array(bulkResultItemSchema),
});
export type BulkResult = z.infer<typeof bulkResultSchema>;

/** The answer of `POST /findings/{id}/rescan` (202): the incremental scan of the item is queued. */
export const rescanResultSchema = z.object({
  queued: z.boolean(),
});
export type RescanResult = z.infer<typeof rescanResultSchema>;

/** The answer of `POST /findings/{id}/revert-censor`: the finding (now a false positive), what
 * became of the document's text, and whether the ledger's censor decision was withdrawn (it stays
 * while another document still holds the value censored). */
export const revertCensorResponseSchema = z.object({
  finding: findingSchema,
  text_restore: textRestoreSchema,
  decision_removed: z.boolean(),
});
export type RevertCensorResponse = z.infer<typeof revertCensorResponseSchema>;

export const findingOrderBySchema = z.enum(["newest", "oldest", "severity"]);
export type FindingOrderBy = z.infer<typeof findingOrderBySchema>;

export type BulkAction = "censor" | "false_positive" | "revert_censor";

/** The most findings one bulk action may carry (the API answers `bulk_too_large` above it). */
export const BULK_MAX_FINDINGS = 50;

/** How often the latest scan is polled while one is queued or running. */
export const SCAN_POLL_INTERVAL_MS = 4000;

export interface ListFindingsParams {
  status?: FindingStatus | undefined;
  category?: FindingCategory | undefined;
  kind?: string | undefined;
  source?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
  orderBy?: FindingOrderBy | undefined;
}

export interface PageParams {
  /** The opaque `next_cursor` of the previous page, passed back untouched. */
  cursor?: string | null | undefined;
  limit?: number | undefined;
}

const BASE = "/admin/sensitive-data";

/** A reason travels trimmed, and not at all when blank (it is optional for a censor). */
function optionalReasonBody(reason: string | undefined): { reason?: string } {
  const trimmed = reason?.trim() ?? "";
  return trimmed === "" ? {} : { reason: trimmed };
}

function requiredReasonBody(reason: string): { reason: string } {
  return { reason: reason.trim() };
}

export const sensitiveDataApi = {
  summary: () => apiClient.get(`${BASE}/summary`, summarySchema),
  list: (params: ListFindingsParams = {}) =>
    apiClient.get(`${BASE}/findings`, findingListSchema, {
      query: {
        status: params.status,
        category: params.category,
        kind: params.kind,
        source: params.source,
        limit: params.limit,
        offset: params.offset,
        order_by: params.orderBy,
      },
    }),
  /** Re-derives the value from the item and counts it elsewhere; only on demand. */
  locations: (id: string) => apiClient.get(`${BASE}/findings/${id}/locations`, locationListSchema),
  censor: (id: string, reason?: string) =>
    apiClient.post(`${BASE}/findings/${id}/censor`, findingSchema, optionalReasonBody(reason)),
  deleteItem: (id: string, reason: string) =>
    apiClient.post(`${BASE}/findings/${id}/delete-item`, findingSchema, requiredReasonBody(reason)),
  falsePositive: (id: string, reason: string) =>
    apiClient.post(
      `${BASE}/findings/${id}/false-positive`,
      findingSchema,
      requiredReasonBody(reason),
    ),
  /** Reopens a false positive, or a resolved finding whose value is still in the document. */
  reopen: (id: string) => apiClient.post(`${BASE}/findings/${id}/reopen`, findingSchema, {}),
  /** Withdraws a censor: the finding becomes a false positive and the text is restored from its
   * source where one exists (`text_restore` says what happened). The reason is mandatory. */
  revertCensor: (id: string, reason: string) =>
    apiClient.post(
      `${BASE}/findings/${id}/revert-censor`,
      revertCensorResponseSchema,
      requiredReasonBody(reason),
    ),
  promote: (id: string) => apiClient.post(`${BASE}/findings/${id}/promote`, findingSchema, {}),
  /** Scans the finding's document again now: the answer to `finding_stale`. */
  rescan: (id: string) => apiClient.post(`${BASE}/findings/${id}/rescan`, rescanResultSchema, {}),
  bulk: (params: { action: BulkAction; findingIds: readonly string[]; reason?: string }) =>
    apiClient.post(`${BASE}/findings/bulk`, bulkResultSchema, {
      action: params.action,
      finding_ids: params.findingIds,
      ...optionalReasonBody(params.reason),
    }),
  triggerScan: () => apiClient.post(`${BASE}/scan`, scanSchema, undefined),
  latestScan: () => apiClient.get(`${BASE}/scans/latest`, scanSchema.nullable()),
  scans: (params: PageParams = {}) =>
    apiClient.get(`${BASE}/scans`, scanListSchema, {
      query: { cursor: params.cursor ?? undefined, limit: params.limit },
    }),
  events: (params: PageParams = {}) =>
    apiClient.get(`${BASE}/events`, eventListSchema, {
      query: { cursor: params.cursor ?? undefined, limit: params.limit },
    }),
  /** `text/csv` with one row per scan or event; fetched with the session token, not a bare link. */
  exportCsv: () => apiClient.getBlob(`${BASE}/export.csv`),
};

/** A scan is in flight while queued or running: it drives polling and the button state. */
export function scanInFlight(scan: Scan | null | undefined): boolean {
  return scan?.status === "queued" || scan?.status === "running";
}

/** Spanish text for the machine-readable codes the sensitive-data endpoints answer with, so the
 * admin never reads the backend's English message. Keyed by `detail.code`. */
export const SENSITIVE_DATA_ERROR_MESSAGES: Record<string, string> = {
  finding_not_found: "El hallazgo ya no existe en este repositorio.",
  finding_terminal:
    "Este hallazgo ya está censurado o eliminado: el valor ya no está en el documento, así que no hay nada más que hacer.",
  finding_stale:
    "El documento cambió desde el último análisis y el hallazgo quedó desactualizado: vuelve a analizar el documento para actualizarlo.",
  invalid_transition: "Esa acción no aplica al hallazgo en su estado actual.",
  reason_required: "Escribe un motivo para continuar.",
  system_owned_item:
    "Este documento pertenece al sistema (autoconocimiento o corpus de evaluación) y no se puede censurar ni eliminar desde aquí. Corrige el archivo en el repositorio mediante un PR.",
  censor_duplicate_content:
    "Al censurar, el documento quedaría idéntico a otro que ya existe. Elimina el documento en lugar de censurarlo.",
  value_not_present: "El valor ya no está en el documento: no hay nada que reabrir.",
  value_censored_elsewhere:
    "Este valor ya fue censurado en otro documento; no se puede marcar como falso positivo aquí.",
  scan_already_running: "Ya hay un análisis en curso. Espera a que termine.",
  scan_disabled: "El análisis está desactivado. Actívalo en Ajustes del sistema.",
  bulk_too_large: "Selecciona como máximo 50 hallazgos por lote.",
  invalid_cursor: "La lista cambió mientras la leías. Recárgala para continuar.",
  enqueue_failed: "No se pudo poner en cola el análisis. Inténtalo de nuevo en unos minutos.",
  fingerprint_secret_missing:
    "Este entorno no tiene configurada la clave con la que se identifican los valores sensibles. Pídele a quien administra el despliegue que defina SENSITIVE_DATA_FINGERPRINT_SECRET.",
  action_failed: "No se pudo completar la acción sobre este hallazgo. Inténtalo de nuevo.",
};

/** The Spanish text for a known error code, else the error's own message. A bare 404 means the
 * finding left the repository between the list and the click. */
export function sensitiveDataErrorText(error: Error): string {
  const code = apiErrorCode(error);
  const known = code !== null ? SENSITIVE_DATA_ERROR_MESSAGES[code] : undefined;
  if (known !== undefined) return known;
  if (error instanceof ApiError && error.status === 404) {
    return "El hallazgo ya no existe en este repositorio.";
  }
  return error.message;
}

/** The Spanish text of a per-finding error code of a bulk result (`null` is a success). */
export function bulkErrorText(code: string | null): string {
  if (code === null) return "";
  return SENSITIVE_DATA_ERROR_MESSAGES[code] ?? `No se pudo procesar (${code}).`;
}
